import { useMemo } from 'react';

import { PrintProps } from './multiple-choice.constants';
import SubActivityPrint from '../sub-activity/sub-activity-print';
import {
  MultipleChoiceLetter,
  MultipleChoiceLetterImage,
  MultipleChoiceStatus,
} from './multiple-choice';
import {
  canShowOptionLetter,
  getOptionsPerColumn,
  getRowAndColumnsValue,
  parseMultipleChoiceTypes,
  sortOptions,
} from './multiple-choice.utils';
import MultipleChoicePrintList from './list/multiple-choice-print-list';
import MultipleChoiceImagePrint from './image/multiple-choice-image-print';
import MultipleChoiceTextImagePrint from './text-image/multiple-choice-text-image-print';
import * as Styled from './multiple-choice-print.style';

const MultipleChoicePrint = (
  props: PrintProps & {
    previewSize?: number;
    showAnswer?: boolean;
  }
) => {
  const { properties, subActivityProperties, showAnswer, previewSize } = props;
  const { multipleChoice } = properties;

  const { isList, isText, isImage, isTextAndImage } = parseMultipleChoiceTypes(
    properties
  );

  const sortedOptions = useMemo(() => sortOptions(multipleChoice.options), [
    multipleChoice.options,
  ]);

  if (isText && isList) {
    return <MultipleChoicePrintList {...props} showAnswer={showAnswer} />;
  }

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <Styled.MultipleChoiceButtons
        role="radiogroup"
        $display={properties.layout?.display}
        $isNoWrap={
          !!(
            properties.multipleChoice.rowsGridLayout &&
            getRowAndColumnsValue(properties.multipleChoice.rowsGridLayout)
              .rows === 1
          )
        }
      >
        {sortedOptions.map((option, index) => {
          const optionsPerColumn = getOptionsPerColumn(
            properties.multipleChoice
          );
          return (
            <Styled.MultipleChoiceItem
              key={index}
              $isText={isText}
              $itemsPerRow={optionsPerColumn}
              $minHeight={multipleChoice.optionMinHeight || 80}
              $hasMarginRight={(index + 1) % optionsPerColumn !== 0}
              $isCorrect={showAnswer ? option.correctAnswerBoolean : undefined}
              $isIncorrect={
                showAnswer ? !option.correctAnswerBoolean : undefined
              }
            >
              {showAnswer && (
                <MultipleChoiceStatus
                  isCorrect={option.correctAnswerBoolean}
                  isIncorrect={!option.correctAnswerBoolean}
                  isPartiallyCorrect={false}
                  isRevealAnswer={false}
                />
              )}

              {isTextAndImage ? (
                <MultipleChoiceTextImagePrint
                  optionMinHeight={multipleChoice.optionMinHeight}
                  imagePosition={multipleChoice.imagePosition}
                  showOptionLetter={multipleChoice.showOptionLetter}
                  option={option}
                  index={index}
                  previewSize={previewSize}
                />
              ) : (
                <Styled.MultipleChoicePrintContent>
                  {isImage ? (
                    <Styled.MultipleChoicePrintImage>
                      <MultipleChoiceImagePrint
                        option={option}
                        previewSize={previewSize}
                      />
                      {canShowOptionLetter(multipleChoice.showOptionLetter) && (
                        <MultipleChoiceLetterImage index={index} />
                      )}
                    </Styled.MultipleChoicePrintImage>
                  ) : (
                    <Styled.MultipleChoicePrintTextContent>
                      {canShowOptionLetter(multipleChoice.showOptionLetter) && (
                        <MultipleChoiceLetter index={index} />
                      )}
                      <Styled.MultipleChoiceButtonText>
                        {option.text}
                      </Styled.MultipleChoiceButtonText>
                    </Styled.MultipleChoicePrintTextContent>
                  )}
                </Styled.MultipleChoicePrintContent>
              )}
            </Styled.MultipleChoiceItem>
          );
        })}
      </Styled.MultipleChoiceButtons>
    </SubActivityPrint>
  );
};

export default MultipleChoicePrint;
